import { DRUM_LANES, type DrumLane } from "../../sequencer/patterns";
import { midiToName } from "../../theory/notes";
import { useLive, useProject, useServices } from "../context";
import { Panel, Select, Slider, pct } from "./controls";

const PAD_LABEL: Record<DrumLane, string> = { kick: "Kick", snare: "Snare", hat: "Hi-hat", clap: "Clap" };
const PAD_KEYS: Record<DrumLane, string> = { kick: "1", snare: "2", hat: "3", clap: "4" };

export function Pads() {
  const { controller } = useServices();
  const padsTrackId = useProject((p) => p.playground.padsTrackId);
  const track = useProject((p) => p.tracks.find((t) => t.id === p.playground.padsTrackId));
  const sample = useLive((s) => s.samples[padsTrackId]);
  const sampler = track?.instrument.kind === "sampler" ? track.instrument : null;

  const setParams = (patch: Partial<NonNullable<typeof sampler>["params"]>) => controller.updateProject((p) => ({
    ...p,
    tracks: p.tracks.map((t) => (t.id === padsTrackId && t.instrument.kind === "sampler" ? { ...t, instrument: { ...t.instrument, params: { ...t.instrument.params, ...patch } } } : t)),
  }));

  const sampleText = !sample || sample.status === "none" ? "No sample loaded"
    : sample.status === "loading" ? `Loading ${sample.name ?? ""}…`
    : sample.status === "missing" ? "Sample missing — load a new one"
    : sample.status === "error" ? `Could not load ${sample.name ?? "sample"}`
    : sample.name ?? "Sample ready";

  return (
    <Panel title="Pads" className="pads-panel">
      <div className="pad-grid">
        {DRUM_LANES.map((lane) => (
          <button key={lane} type="button" className={`pad pad-${lane}`} onPointerDown={(e) => { e.preventDefault(); controller.triggerDrum(lane); }}
            onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !e.repeat) { e.preventDefault(); controller.triggerDrum(lane); } }}
            aria-keyshortcuts={PAD_KEYS[lane]}>
            <span className="pad-name">{PAD_LABEL[lane]}</span>
            <span className="pad-key" aria-hidden="true">{PAD_KEYS[lane]}</span>
          </button>
        ))}
        <button type="button" className={`pad pad-sample ${sample?.status === "ready" ? "" : "empty"}`} onPointerDown={(e) => { e.preventDefault(); controller.triggerSample(); }}
          onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !e.repeat) { e.preventDefault(); controller.triggerSample(); } }}
          aria-keyshortcuts="5" aria-describedby="sample-status">
          <span className="pad-name">Sample</span>
          <span className="pad-key" aria-hidden="true">5</span>
        </button>
      </div>
      <div className="sampler-controls">
        <p id="sample-status" className={`sample-status ${sample?.status ?? "none"}`} role="status">{sampleText}</p>
        <label className="file-button primary">
          Load sample…
          <input type="file" accept="audio/*,.wav,.mp3,.ogg,.flac,.m4a,.aac" className="sr-only" onChange={(e) => {
            const f = e.currentTarget.files?.[0];
            e.currentTarget.value = "";
            if (f) void controller.importSample(f);
          }} />
        </label>
        {sampler && (
          <>
            <Select label="Pad mode" value={sampler.params.mode} options={[{ value: "oneshot", label: "One-shot" }, { value: "gate", label: "Gate (stop on release)" }]} onChange={(mode) => setParams({ mode })} />
            <Slider label="Root note" value={sampler.params.rootNote} min={24} max={96} step={1} format={(n) => midiToName(n)} onChange={(rootNote) => setParams({ rootNote })} hint="Pitch at which the sample plays unchanged" />
            <Slider label="Sample volume" value={sampler.params.volume} min={0} max={1} format={pct} onChange={(volume) => setParams({ volume })} />
          </>
        )}
        <p className="hint">Imported samples stay in this browser. Pitch follows song transposition via playback speed, which also changes their length.</p>
      </div>
    </Panel>
  );
}
